import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { PasswordInput } from '../ui/password-strength';
import { MerchantComboSelect } from '../ui/merchant-combo-select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Users as UsersIcon, 
  Filter, 
  Plus, 
  Edit,
  Trash2,
  RefreshCw,
  X,
  Shield,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  RotateCcw,
  Mail,
  Calendar,
  UserX,
  UserCheck,
  Crown,
  Lock,
  Store,
  Search
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const SUPER_ADMIN_EMAIL = 'admin@paygateway.com';

const Users = () => {
  const { token, user, isAdmin, backendUrl } = useAuth();
  
  // Check if current user is super admin
  const isSuperAdmin = user?.email === SUPER_ADMIN_EMAIL && user?.is_admin;
  
  // Dynamic API URL for dev/prod environments
  const API = `${backendUrl}/api`;
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [availableMerchants, setAvailableMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    role: 'all',      // 'all', 'admin', 'user'
    status: 'all',    // 'all', 'active', 'inactive', 'blocked'
    platform: 'all'   // 'all' or platform_id
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0
  });
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formKey, setFormKey] = useState(0); // Add key for form reset

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    platforms: [],
    merchants: [],
    is_admin: false
  });
  const [passwordValid, setPasswordValid] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  useEffect(() => {
    if (token && user?.is_admin) {
      fetchUsers();
      fetchPlatforms();
    }
  }, [token, user]);

  // Apply client-side filtering when users, searchQuery, or filters change
  useEffect(() => {
    let result = [...users];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(u => 
        u.name?.toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query)
      );
    }
    
    // Role filter
    if (filters.role !== 'all') {
      if (filters.role === 'admin') {
        result = result.filter(u => u.is_admin);
      } else if (filters.role === 'user') {
        result = result.filter(u => !u.is_admin);
      }
    }
    
    // Status filter
    if (filters.status !== 'all') {
      if (filters.status === 'active') {
        result = result.filter(u => u.is_active && !u.account_locked);
      } else if (filters.status === 'inactive') {
        result = result.filter(u => !u.is_active);
      } else if (filters.status === 'blocked') {
        result = result.filter(u => u.account_locked);
      }
    }
    
    // Platform filter
    if (filters.platform !== 'all') {
      result = result.filter(u => u.platforms?.includes(filters.platform));
    }
    
    // Update pagination
    const total = result.length;
    const totalPages = Math.ceil(total / pagination.per_page);
    
    // Apply pagination
    const startIndex = (pagination.page - 1) * pagination.per_page;
    const paginatedResult = result.slice(startIndex, startIndex + pagination.per_page);
    
    setFilteredUsers(paginatedResult);
    setPagination(prev => ({
      ...prev,
      total,
      total_pages: totalPages
    }));
  }, [users, searchQuery, filters, pagination.page, pagination.per_page]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [searchQuery, filters]);

  // Fetch available merchants when platforms change in newUser or editingUser
  useEffect(() => {
    if (newUser.platforms.length > 0) {
      fetchAvailableMerchants(newUser.platforms);
    } else {
      setAvailableMerchants([]);
    }
  }, [newUser.platforms]);

  useEffect(() => {
    if (editingUser?.platforms?.length > 0) {
      fetchAvailableMerchants(editingUser.platforms);
    }
  }, [editingUser?.platforms]);

  const fetchAvailableMerchants = async (platformIds) => {
    if (!token || !platformIds.length) {
      setAvailableMerchants([]);
      return;
    }
    
    try {
      const params = new URLSearchParams();
      platformIds.forEach(id => params.append('platform_ids', id));
      
      const response = await axios.get(`${API}/admin/users/available-merchants?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableMerchants(response.data.merchants || []);
    } catch (err) {
      console.error('Error fetching available merchants:', err);
      setAvailableMerchants([]);
    }
  };

  const fetchUsers = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        per_page: pagination.per_page.toString()
      });

      const response = await axios.get(`${API}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sort users: Blocked users first, then active, then inactive
      const sortedUsers = response.data.users.sort((a, b) => {
        // Priority: Blocked > Active > Inactive
        if (a.account_locked && !b.account_locked) return -1;
        if (!a.account_locked && b.account_locked) return 1;
        
        // Then by active status
        if (a.is_active && !b.is_active) return -1;
        if (!a.is_active && b.is_active) return 1;
        
        // Finally by name
        return a.name.localeCompare(b.name);
      });
      
      setUsers(sortedUsers);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || response.data.users.length,
        total_pages: Math.ceil((response.data.total || response.data.users.length) / pagination.per_page)
      }));
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatforms = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/platforms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlatforms(response.data.platforms);
    } catch (err) {
      console.error('Error fetching platforms:', err);
    }
  };

  const createUser = async () => {
    if (!token || !passwordValid) return;
    
    try {
      await axios.post(`${API}/users/create`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowCreateDialog(false);
      setNewUser({
        name: '',
        email: '',
        password: '',
        platforms: [],
        merchants: [],
        is_admin: false
      });
      setPasswordValid(false);
      setPasswordErrors([]);
      setAvailableMerchants([]);
      fetchUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Failed to create user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openEditDialog = (userItem) => {
    setEditingUser({
      ...userItem,
      merchants: userItem.merchants || [],
      password: '' // Don't show existing password
    });
    setShowEditDialog(true);
  };

  const updateUser = async () => {
    if (!token || !editingUser) return;
    
    try {
      const updateData = {
        name: editingUser.name,
        email: editingUser.email,
        platforms: editingUser.platforms || [],
        merchants: editingUser.merchants || [],
        is_admin: editingUser.is_admin,
        is_active: editingUser.is_active
      };

      // Only include password if it's provided
      if (editingUser.password && editingUser.password.trim()) {
        updateData.password = editingUser.password;
      }

      console.log('[UpdateUser] Sending update:', updateData);
      
      const response = await axios.patch(`${API}/admin/users/${editingUser.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[UpdateUser] Response:', response.data);
      
      setShowEditDialog(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      console.error('Error updating user:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to update user';
      setError(errorMsg);
    }
  };

  const toggleUserStatus = async (userId, userName, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!token || !window.confirm(`Are you sure you want to ${action} user "${userName}"?`)) return;
    
    try {
      console.log(`[ToggleUserStatus] ${action} user ${userName} (${userId}), current: ${currentStatus}, new: ${!currentStatus}`);
      
      // Optimistically update the UI
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { ...user, is_active: !currentStatus, account_locked: !currentStatus ? false : user.account_locked }
            : user
        )
      );
      
      const response = await axios.patch(`${API}/admin/users/${userId}`, 
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log(`[ToggleUserStatus] Response:`, response.data);
      
      // If activating, also unblock the user if they're blocked
      if (!currentStatus) {
        try {
          await axios.post(`${API}/auth/admin/unlock-user`,
            { user_id: userId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log(`[ToggleUserStatus] User unblocked during activation`);
        } catch (unlockErr) {
          console.error(`[ToggleUserStatus] Failed to unblock user during activation:`, unlockErr);
          // Continue even if unblock fails
        }
      }
      
      // Show success message
      alert(`User ${userName} has been ${action}d successfully.`);
      
      // Refresh users list to get the latest data from server
      await fetchUsers();
    } catch (err) {
      console.error(`[ToggleUserStatus] Error ${action}ing user:`, err);
      const errorMsg = err.response?.data?.detail || `Failed to ${action} user`;
      setError(errorMsg);
      alert(`Failed to ${action} user: ${errorMsg}`);
      
      // Revert optimistic update on error
      await fetchUsers();
    }
  };

  const unlockUser = async (userId, userName) => {
    if (!token || !window.confirm(`Are you sure you want to unblock user "${userName}"? This will reset all failed login attempts and allow them to login.`)) return;
    
    try {
      console.log(`[UnlockUser] Unblocking user ${userName} (${userId})`);
      
      // Optimistically update the UI
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { ...user, account_locked: false, locked_until: null }
            : user
        )
      );
      
      const response = await axios.post(`${API}/auth/admin/unlock-user`,
        { user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log(`[UnlockUser] Response:`, response.data);
      
      // Show success message
      alert(`User ${userName} has been unblocked successfully.`);
      
      // Refresh users list to get the latest data from server
      await fetchUsers();
    } catch (err) {
      console.error(`[UnlockUser] Error unblocking user:`, err);
      const errorMsg = err.response?.data?.detail || 'Failed to unblock user';
      setError(errorMsg);
      alert(`Failed to unblock user: ${errorMsg}`);
      
      // Revert optimistic update on error
      await fetchUsers();
    }
  };

  const resetMFA = async (userId, userName) => {
    if (!token || !window.confirm(`Are you sure you want to reset MFA for "${userName}"? They will need to set up MFA again on their next login.`)) return;
    
    try {
      await axios.post(`${API}/auth/mfa/admin-reset`, 
        { user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show success message
      alert(`MFA has been reset for ${userName}. They will need to set up MFA again on their next login.`);
      
      // Refresh users to update MFA status
      fetchUsers();
    } catch (err) {
      console.error('Error resetting MFA:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to reset MFA';
      setError(errorMsg);
      alert(`Failed to reset MFA: ${errorMsg}`);
    }
  };

  const toggleMFARequirement = async (userId, userName, currentStatus) => {
    const action = currentStatus ? 'disable' : 'enable';
    if (!token || !window.confirm(`Are you sure you want to ${action} MFA requirement for "${userName}"?`)) return;
    
    try {
      await axios.post(`${API}/auth/mfa/admin-toggle/${userId}`,
        { mfa_required: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show success message
      alert(`MFA requirement ${action}d for ${userName}.`);
      
      // Refresh users to update MFA status
      fetchUsers();
    } catch (err) {
      console.error('Error toggling MFA requirement:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to toggle MFA requirement';
      setError(errorMsg);
      alert(`Failed to toggle MFA requirement: ${errorMsg}`);
    }
  };

  const getPlatformNames = (platformIds) => {
    if (!platformIds || platformIds.length === 0) return 'No platforms';
    return platformIds.map(id => {
      const platform = platforms.find(p => p.id === id);
      return platform ? platform.name : id;
    }).join(', ');
  };

  if (!user?.is_admin) {
    return (
      <div className="space-y-8 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Shield className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Access Denied</h3>
            <p className="text-muted-foreground">Only administrators can access user management.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading && users.length === 0) {
    return (
      <div className="space-y-8 p-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2" style={{ color: 'var(--input-text-color)' }}>
            <UserCheck className="w-8 h-8 text-indigo-500" />
            User Management
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage system users and their platform access
            {isSuperAdmin && (
              <Badge className="ml-2 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                <Crown className="h-3 w-3 mr-1" />
                Super Admin
              </Badge>
            )}
          </p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (open) {
            // Reset form when opening modal
            setNewUser({
              name: '',
              email: '',
              password: '',
              platforms: [],
              merchants: [],
              is_admin: false
            });
            setPasswordValid(false);
            setPasswordErrors([]);
            setAvailableMerchants([]);
            setFormKey(prev => prev + 1); // Force component remount
          }
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
            </DialogHeader>
            <form autoComplete="off" key={`form-${formKey}`}>
            <div key={formKey} className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Full Name</label>
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  placeholder="Enter full name"
                  autoComplete="off"
                  name={`create-user-name-${formKey}`}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  placeholder="user@company.com"
                  autoComplete="off"
                  name={`create-user-email-${formKey}`}
                />
              </div>
              
              <div className="space-y-2 col-span-2">
                <PasswordInput
                  value={newUser.password}
                  onChange={(password) => setNewUser({...newUser, password})}
                  username={newUser.name}
                  email={newUser.email}
                  label="Password"
                  placeholder="Enter secure password"
                  required={true}
                  name={`create-user-password-${formKey}`}
                  onValidationChange={(isValid, errors) => {
                    setPasswordValid(isValid);
                    setPasswordErrors(errors);
                  }}
                  showRequirements={false}
                />
                {newUser.password && !passwordValid && (
                  <div className="text-xs text-gray-600 p-2 bg-gray-50 rounded border">
                    <strong>PCI DSS Requirements:</strong> 12+ characters, uppercase/lowercase, numbers, special characters (!@#$%^&*), no personal info
                  </div>
                )}
              </div>
              
              <div className="space-y-2 col-span-2">
                <label className="text-sm font-medium">Platform Access</label>
                <Select 
                  value={newUser.platforms.length > 0 ? newUser.platforms[0] : ''} 
                  onValueChange={(value) => {
                    if (value && !newUser.platforms.includes(value)) {
                      // When platform changes, also clear merchants that don't belong to new platforms
                      const newPlatforms = [...newUser.platforms, value];
                      const validMerchants = newUser.merchants.filter(mid => {
                        const merchant = availableMerchants.find(m => m.id === mid);
                        return merchant && newPlatforms.includes(merchant.platform_id);
                      });
                      setNewUser({...newUser, platforms: newPlatforms, merchants: validMerchants});
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select platforms" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Super admin sees all platforms, regular admin only their own */}
                    {(isSuperAdmin ? platforms : platforms.filter(p => user?.platforms?.includes(p.id))).map((platform) => (
                      <SelectItem key={platform.id} value={platform.id}>
                        {platform.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {newUser.platforms.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newUser.platforms.map((platformId) => {
                      const platform = platforms.find(p => p.id === platformId);
                      return (
                        <Badge key={platformId} variant="outline" className="text-xs">
                          {platform?.name || platformId}
                          <X 
                            className="h-3 w-3 ml-1 cursor-pointer" 
                            onClick={() => {
                              const newPlatforms = newUser.platforms.filter(id => id !== platformId);
                              // Also remove merchants from the removed platform
                              const validMerchants = newUser.merchants.filter(mid => {
                                const merchant = availableMerchants.find(m => m.id === mid);
                                return merchant && newPlatforms.includes(merchant.platform_id);
                              });
                              setNewUser({
                                ...newUser, 
                                platforms: newPlatforms,
                                merchants: validMerchants
                              });
                            }}
                          />
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Merchant Access Section */}
              <div className="space-y-2 col-span-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Store className="h-4 w-4" />
                  Merchant Access
                </label>
                <MerchantComboSelect
                  merchants={availableMerchants}
                  selectedMerchants={newUser.merchants}
                  onSelect={(merchantId) => {
                    if (!newUser.merchants.includes(merchantId)) {
                      setNewUser({...newUser, merchants: [...newUser.merchants, merchantId]});
                    }
                  }}
                  onRemove={(merchantId) => {
                    setNewUser({
                      ...newUser,
                      merchants: newUser.merchants.filter(id => id !== merchantId)
                    });
                  }}
                  onClearAll={() => {
                    setNewUser({...newUser, merchants: []});
                  }}
                  disabled={newUser.platforms.length === 0}
                  placeholder={newUser.platforms.length ? "Search and select merchants..." : "Select platforms first"}
                  platforms={platforms}
                />
                {newUser.platforms.length === 0 && (
                  <p className="text-xs text-muted-foreground">Select platforms first to see available merchants</p>
                )}
              </div>
              
              <div className="space-y-2 col-span-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_admin"
                    checked={newUser.is_admin}
                    onChange={(e) => setNewUser({...newUser, is_admin: e.target.checked})}
                    disabled={!isSuperAdmin}
                  />
                  <label htmlFor="is_admin" className="text-sm font-medium">
                    Administrator privileges
                    {!isSuperAdmin && (
                      <span className="text-xs text-muted-foreground ml-2">
                        (Super admin only)
                      </span>
                    )}
                  </label>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => {
                setShowCreateDialog(false);
                setNewUser({
                  name: '',
                  email: '',
                  password: '',
                  platforms: [],
                  merchants: [],
                  is_admin: false
                });
                setPasswordValid(false);
                setPasswordErrors([]);
                setAvailableMerchants([]);
              }}>
                Cancel
              </Button>
              <Button onClick={createUser} disabled={!newUser.name || !newUser.email || !newUser.password || !passwordValid}>
                Create User
              </Button>
            </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Full Name</label>
                <Input
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                  placeholder="Enter full name"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  placeholder="user@company.com"
                  autoComplete="off"
                />
              </div>
              
              <div className="space-y-2 col-span-2">
                <label className="text-sm font-medium">New Password (leave blank to keep current)</label>
                <Input
                  type="password"
                  value={editingUser.password}
                  onChange={(e) => setEditingUser({...editingUser, password: e.target.value})}
                  placeholder="Enter new password"
                />
              </div>
              
              <div className="space-y-2 col-span-2">
                <label className="text-sm font-medium">Platform Access</label>
                {/* Super Admin being edited - read-only, has access to all platforms */}
                {editingUser.email === SUPER_ADMIN_EMAIL ? (
                  <div className="space-y-2">
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-md">
                      <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                        <Crown className="h-4 w-4" />
                        <span className="font-medium">All Platforms Access</span>
                      </div>
                      <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                        Super Admin has unrestricted access to all platforms
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Select 
                      value={''} 
                      onValueChange={(value) => {
                        if (value && !editingUser.platforms.includes(value)) {
                          setEditingUser({...editingUser, platforms: [...editingUser.platforms, value]});
                        }
                      }}
                      disabled={editingUser.is_admin && !isSuperAdmin}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={editingUser.is_admin && !isSuperAdmin ? "Only super admin can modify admin platforms" : "Add platform access"} />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Super admin sees all platforms, regular admin only their own */}
                        {(isSuperAdmin ? platforms : platforms.filter(p => user?.platforms?.includes(p.id))).map((platform) => (
                          <SelectItem key={platform.id} value={platform.id}>
                            {platform.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {editingUser.is_admin && !isSuperAdmin && (
                      <p className="text-xs text-muted-foreground">
                        <Lock className="h-3 w-3 inline mr-1" />
                        Only super admin can modify platform access for admin users
                      </p>
                    )}
                    
                    {editingUser.platforms.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {editingUser.platforms.map((platformId) => {
                          const platform = platforms.find(p => p.id === platformId);
                          const canRemove = !editingUser.is_admin || isSuperAdmin;
                          return (
                            <Badge key={platformId} variant="outline" className="text-xs">
                              {platform?.name || platformId}
                              {canRemove && (
                                <X 
                                  className="h-3 w-3 ml-1 cursor-pointer" 
                                  onClick={() => {
                                    const newPlatforms = editingUser.platforms.filter(id => id !== platformId);
                                    // Also remove merchants from the removed platform
                                    const validMerchants = (editingUser.merchants || []).filter(mid => {
                                      const merchant = availableMerchants.find(m => m.id === mid);
                                      return merchant && newPlatforms.includes(merchant.platform_id);
                                    });
                                    setEditingUser({
                                      ...editingUser, 
                                      platforms: newPlatforms,
                                      merchants: validMerchants
                                    });
                                  }}
                                />
                              )}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Merchant Access Section */}
              <div className="space-y-2 col-span-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Store className="h-4 w-4" />
                  Merchant Access
                </label>
                {/* Super Admin being edited - read-only, has access to all merchants */}
                {editingUser.email === SUPER_ADMIN_EMAIL ? (
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-md">
                    <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                      <Store className="h-4 w-4" />
                      <span className="font-medium">All Merchants Access</span>
                    </div>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                      Super Admin has unrestricted access to all merchants
                    </p>
                  </div>
                ) : (
                  <>
                    <MerchantComboSelect
                      merchants={availableMerchants}
                      selectedMerchants={editingUser.merchants || []}
                      onSelect={(merchantId) => {
                        if (!editingUser.merchants?.includes(merchantId)) {
                          setEditingUser({...editingUser, merchants: [...(editingUser.merchants || []), merchantId]});
                        }
                      }}
                      onRemove={(merchantId) => {
                        setEditingUser({
                          ...editingUser,
                          merchants: (editingUser.merchants || []).filter(id => id !== merchantId)
                        });
                      }}
                      onClearAll={() => {
                        setEditingUser({...editingUser, merchants: []});
                      }}
                      disabled={editingUser.platforms?.length === 0}
                      placeholder={editingUser.platforms?.length ? "Search and select merchants..." : "Select platforms first"}
                      platforms={platforms}
                    />
                    {editingUser.platforms?.length === 0 && (
                      <p className="text-xs text-muted-foreground">Select platforms first to see available merchants</p>
                    )}
                  </>
                )}
              </div>
              
              <div className="space-y-2 col-span-2">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit_is_admin"
                      checked={editingUser.is_admin}
                      onChange={(e) => setEditingUser({...editingUser, is_admin: e.target.checked})}
                      disabled={!isSuperAdmin || editingUser.email === SUPER_ADMIN_EMAIL}
                    />
                    <label htmlFor="edit_is_admin" className="text-sm font-medium">
                      Administrator privileges
                      {!isSuperAdmin && (
                        <span className="text-xs text-muted-foreground ml-2">
                          (Super admin only)
                        </span>
                      )}
                      {editingUser.email === SUPER_ADMIN_EMAIL && (
                        <Badge className="ml-2 bg-purple-100 text-purple-800">
                          <Crown className="h-3 w-3 mr-1" />
                          Super Admin
                        </Badge>
                      )}
                    </label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit_is_active"
                      checked={editingUser.is_active}
                      onChange={(e) => setEditingUser({...editingUser, is_active: e.target.checked})}
                      disabled={editingUser.email === SUPER_ADMIN_EMAIL}
                    />
                    <label htmlFor="edit_is_active" className="text-sm font-medium">
                      Active account
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={updateUser} disabled={!editingUser?.name || !editingUser?.email}>
              Update User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filters:</span>
        </div>
        
        {/* Search Input */}
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="users-search-input"
          />
        </div>

        {/* Role Filter */}
        <Select value={filters.role} onValueChange={(value) => setFilters({...filters, role: value})}>
          <SelectTrigger className="w-36" data-testid="users-role-filter">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="user">User</SelectItem>
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
          <SelectTrigger className="w-36" data-testid="users-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="blocked">Blocked</SelectItem>
          </SelectContent>
        </Select>

        {/* Platform Filter */}
        <Select value={filters.platform} onValueChange={(value) => setFilters({...filters, platform: value})}>
          <SelectTrigger className="w-44" data-testid="users-platform-filter">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Platforms</SelectItem>
            {platforms.map((platform) => (
              <SelectItem key={platform.id} value={platform.id}>
                {platform.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Clear Filters Button */}
        {(searchQuery || filters.role !== 'all' || filters.status !== 'all' || filters.platform !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchQuery('');
              setFilters({ role: 'all', status: 'all', platform: 'all' });
            }}
            data-testid="users-clear-filters"
          >
            <X className="h-4 w-4 mr-1" />
            Clear Filters
          </Button>
        )}

        {/* Refresh Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={fetchUsers}
          disabled={loading}
          data-testid="users-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="h-5 w-5" />
            Users ({pagination.total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-8">
              <UsersIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No users found</h3>
              <p className="text-muted-foreground">
                {users.length === 0 ? 'Create your first user to get started.' : 'No users match your current filters.'}
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Platform Access</TableHead>
                    <TableHead>Merchants</TableHead>
                    <TableHead>MFA Status</TableHead>
                    <TableHead>MFA Required</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Last Login</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((userItem) => {
                    const isUserSuperAdmin = userItem.email === SUPER_ADMIN_EMAIL && userItem.is_admin;
                    const canEdit = isSuperAdmin || (!userItem.is_admin && !isUserSuperAdmin);
                    const canDelete = isSuperAdmin ? !isUserSuperAdmin : (!userItem.is_admin && !isUserSuperAdmin);
                    
                    return (
                    <TableRow key={userItem.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {userItem.name}
                            {isUserSuperAdmin && (
                              <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                <Crown className="h-3 w-3 mr-1" />
                                Super
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground flex items-center">
                            <Mail className="h-3 w-3 mr-1" />
                            {userItem.email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {userItem.is_admin ? (
                          <Badge className="bg-red-100 text-red-800">
                            <ShieldCheck className="h-3 w-3 mr-1" />
                            Admin
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <Shield className="h-3 w-3 mr-1" />
                            User
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {getPlatformNames(userItem.platforms)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {userItem.merchants?.length > 0 ? (
                            <Badge variant="outline" className="text-xs">
                              <Store className="h-3 w-3 mr-1" />
                              {userItem.merchants.length} merchant{userItem.merchants.length !== 1 ? 's' : ''}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">
                              {userItem.is_admin ? 'All (Admin)' : 'None'}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {userItem.mfa_enabled ? (
                            <Badge className="bg-green-100 text-green-800">
                              <ShieldCheck className="h-3 w-3 mr-1" />
                              Enabled
                            </Badge>
                          ) : userItem.mfa_required ? (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              <ShieldAlert className="h-3 w-3 mr-1" />
                              Setup Pending
                            </Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-800">
                              <ShieldX className="h-3 w-3 mr-1" />
                              Not Set
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {userItem.mfa_required ? (
                          <Badge className="bg-blue-100 text-blue-800">
                            <ShieldCheck className="h-3 w-3 mr-1" />
                            Required
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600">
                            <ShieldX className="h-3 w-3 mr-1" />
                            Optional
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {userItem.account_locked ? (
                          <Badge className="bg-red-100 text-red-800">
                            <UserX className="h-3 w-3 mr-1" />
                            Blocked
                          </Badge>
                        ) : userItem.is_active ? (
                          <Badge className="bg-green-100 text-green-800">
                            <UserCheck className="h-3 w-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-800">
                            <UserX className="h-3 w-3 mr-1" />
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {new Date(userItem.created_at).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {userItem.last_login ? (
                          new Date(userItem.last_login).toLocaleDateString()
                        ) : (
                          'Never'
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              disabled={!canEdit && !canDelete}
                              data-testid={`user-actions-${userItem.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            {canEdit && (
                              <DropdownMenuItem onClick={() => openEditDialog(userItem)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                            )}
                            {!canEdit && (
                              <DropdownMenuItem disabled className="text-muted-foreground">
                                <Lock className="h-4 w-4 mr-2" />
                                {isUserSuperAdmin ? 'Super Admin' : 'Admin User (Read Only)'}
                              </DropdownMenuItem>
                            )}
                            {isAdmin && canEdit && (
                              <DropdownMenuItem 
                                onClick={() => toggleMFARequirement(userItem.id, userItem.name, userItem.mfa_required)}
                                className="text-blue-600"
                              >
                                {userItem.mfa_required ? (
                                  <>
                                    <ShieldX className="h-4 w-4 mr-2" />
                                    Disable MFA
                                  </>
                                ) : (
                                  <>
                                    <ShieldCheck className="h-4 w-4 mr-2" />
                                    Enable MFA
                                  </>
                                )}
                              </DropdownMenuItem>
                            )}
                            {userItem.mfa_enabled && canEdit && (
                              <DropdownMenuItem 
                                onClick={() => resetMFA(userItem.id, userItem.name)}
                                className="text-orange-600"
                              >
                                <RotateCcw className="h-4 w-4 mr-2" />
                                Reset MFA
                              </DropdownMenuItem>
                            )}
                            {userItem.account_locked && canEdit && (
                              <DropdownMenuItem 
                                onClick={() => unlockUser(userItem.id, userItem.name)}
                                className="text-blue-600"
                              >
                                <ShieldCheck className="h-4 w-4 mr-2" />
                                Unblock Account
                              </DropdownMenuItem>
                            )}
                            {canEdit && !userItem.is_super_admin && (
                              <DropdownMenuItem 
                                className={userItem.is_active ? "text-orange-600" : "text-green-600"}
                                onClick={() => toggleUserStatus(userItem.id, userItem.name, userItem.is_active)}
                              >
                                {userItem.is_active ? (
                                  <>
                                    <UserX className="h-4 w-4 mr-2" />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <UserCheck className="h-4 w-4 mr-2" />
                                    Activate
                                  </>
                                )}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} users
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
            >
              Previous
            </Button>
            <span className="text-sm">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.total_pages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;